import React from 'react';
import LoginPage from './pages/Login/LoginPage';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import AuthGuard from './components/AuthGuard';
import HomePage from './pages/Home/HomePage';
import { useFetchUser } from './hooks/useFetchUser';
import PrimaryGuardian from './pages/PrimaryGuardian/PrimaryGuardian';

import { default as AdditionalGuardianList } from './pages/AdditionalGuardian/List';
import AdditionalGuardian from './pages/AdditionalGuardian/AdditionalGuardian';
import NewAdditionalGuardian from './pages/AdditionalGuardian/New';

import { default as EmergencyContactList } from './pages/EmergencyContact/List';
import EmergencyContact from './pages/EmergencyContact/EmergencyContact';
import NewEmergencyContact from './pages/EmergencyContact/New';

import { default as StudentList } from './pages/Student/List';
import Student from './pages/Student/Student';
import Ledger from './pages/Ledger/Ledger';
import StudentSchedule from './pages/StudentSchedule/StudentSchedule';
import NonSchoolDayCampEnrollment from './pages/NonSchoolDayCampEnrollment/NonSchoolDayCampEnrollment';
import AutoPay from './pages/AutoPay/AutoPay';
import AttendanceTrackerPage from './pages/Attendance/AttendanceTrackerPage';

// Admin pages
import { default as AdminFamiliesList } from './pages/Admin/Families/List';
import AdminFamilyInfo from './pages/Admin/Families/FamilyInfo';
import AdminStudentInfo from './pages/Admin/Families/StudentInfo';
import ElopImport from './pages/Admin/Elop/Import';
import { default as DelinquentAccounts } from './pages/Admin/Reports/Delinquent';
import { default as CreditAccounts } from './pages/Admin/Reports/Credit';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { resolveStripePublishableKey } from './utils/resolveStripePublishableKey';

const stripePromise = loadStripe(
  resolveStripePublishableKey(process.env.REACT_STRIPE_PUBLIC)
);

const App: React.FC = () => {
  useFetchUser();

  return (
    <Elements stripe={stripePromise}>
      <div id="App">
        <Router>
          <Routes>
            <Route path="/login" Component={LoginPage} />
            <Route
              path="/"
              element={
                <AuthGuard>
                  <HomePage />
                </AuthGuard>
              }
            />
            <Route
              path="/primary"
              element={
                <AuthGuard>
                  <PrimaryGuardian />
                </AuthGuard>
              }
            />
            <Route
              path="/additional-guardians"
              element={
                <AuthGuard>
                  <AdditionalGuardianList />
                </AuthGuard>
              }
            />
            <Route
              path="/additional-guardian/new"
              element={
                <AuthGuard>
                  <NewAdditionalGuardian />
                </AuthGuard>
              }
            />
            <Route
              path="/additional-guardian/:id"
              element={
                <AuthGuard>
                  <AdditionalGuardian />
                </AuthGuard>
              }
            />
            <Route
              path="/emergency-contacts"
              element={
                <AuthGuard>
                  <EmergencyContactList />
                </AuthGuard>
              }
            />
            <Route
              path="/emergency-contact/new"
              element={
                <AuthGuard>
                  <NewEmergencyContact />
                </AuthGuard>
              }
            />
            <Route
              path="/emergency-contact/:id"
              element={
                <AuthGuard>
                  <EmergencyContact />
                </AuthGuard>
              }
            />
            <Route
              path="/students"
              element={
                <AuthGuard>
                  <StudentList />
                </AuthGuard>
              }
            />
            <Route
              path="/student/new"
              element={
                <AuthGuard>
                  <Student />
                </AuthGuard>
              }
            />
            <Route
              path="/student/:id"
              element={
                <AuthGuard>
                  <Student />
                </AuthGuard>
              }
            />
            <Route
              path="/student/:id/schedule"
              element={
                <AuthGuard>
                  <StudentSchedule />
                </AuthGuard>
              }
            />
            <Route
              path="/attendance"
              element={
                <AuthGuard>
                  <AttendanceTrackerPage />
                </AuthGuard>
              }
            />
            <Route
              path="/ledger"
              element={
                <AuthGuard>
                  <Ledger />
                </AuthGuard>
              }
            />
            <Route
              path="/NSDCamps"
              element={
                <AuthGuard>
                  <NonSchoolDayCampEnrollment />
                </AuthGuard>
              }
            />
            <Route
              path="/AutoPay"
              element={
                <AuthGuard>
                  <AutoPay />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/families"
              element={
                <AuthGuard>
                  <AdminFamiliesList />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/families/:primaryId"
              element={
                <AuthGuard>
                  <AdminFamilyInfo />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/students/:studentId"
              element={
                <AuthGuard>
                  <AdminStudentInfo />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/elop"
              element={
                <AuthGuard>
                  <ElopImport />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/reports/delinquent"
              element={
                <AuthGuard>
                  <DelinquentAccounts />
                </AuthGuard>
              }
            />
            <Route
              path="/admin/reports/credit"
              element={
                <AuthGuard>
                  <CreditAccounts />
                </AuthGuard>
              }
            />
          </Routes>
        </Router>
      </div>
    </Elements>
  );
};

export default App;
